import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { CompareProgressbar, PercentageBarCellRounded } from "app/components/React/Table/cells";
import type { IWidgetModelTypesType, IWidgetModelTypesWebSource } from "app/components/widget/widget-types/Widget";
import { useLoading } from "app/custom-hooks/loadingHook";
import { channels } from "app/pages/website-analysis/traffic-sources/search/tabs/SearchOverviewGraph/SearchTrafficInsights/typesAndConstants";
import { SearchOverviewTopMiniFlexTable } from "app/pages/website-analysis/traffic-sources/search/tabs/SearchOverviewTraffic/SearchOverviewTopMiniFlexTable";
import type {
    IWidgetWrapperProps,
    IWidgetSingleWrapperProps,
} from "app/pages/website-analysis/traffic-sources/search/tabs/SearchOverviewTraffic/Types";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { useWebsitesFavicons } from "app/services/sitesService/hooks/useWebsitesFavicons";
import {
    getCompareBrandedNonBranded,
    getCompareOrganicVSPaid,
    getCompareTotalVisitsData,
    getMMXTrafficData,
} from "app/services/WebsiteAnalysis/SearchOverviewApiService";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

type KeyData = { [key: string]: Record<string, number> };

interface IMMXData {
    Data: Record<string, KeyData>;
}

interface IData {
    data: {
        TotalCount: number;
        Data: IOrganicVSPaidData[] | ITotalVisitsData[];
    };
}

interface IOrganicVSPaidData {
    OrganicPaidVisits: number[];
    Domain: string;
    Favicon: string;
}

interface ITotalVisitsData {
    SearchTotal: number;
    ShareOfVisits: number;
    Domain: string;
    Favicon: string;
}

export const SearchOverviewTableCompareWrapper: FC<IWidgetSingleWrapperProps> = (props) => {
    const { apiParams, apiParamsObj } = props;
    const { webSource, key } = apiParams;
    const dashboardProps = {
        modelType: "fromWebsite",
        metric: "SearchVisitsOverviewV2",
        type: "SearchOverviewTotalVisitsTable" as IWidgetModelTypesType,
    };
    const title = "analysis.source.search.keywords.header.searchvisits";
    const tooltipsMap = {
        [devicesTypes.DESKTOP]: "analysis.source.search.overview.totalvisits.desktop.tooltip",
        [devicesTypes.MOBILE]: "analysis.source.search.overview.totalvisits.mobile.tooltip",
        [devicesTypes.TOTAL]: "analysis.source.search.overview.totalvisits.alltraffic.tooltip",
    };
    const fieldName = "SearchTotal";
    const displayName = "analysis.source.search.overview.totalvisits.column.name";
    const trendCall = CompareProgressbar;
    const isTotal = webSource === devicesTypes.TOTAL;
    const apiCall = isTotal ? getMMXTrafficData : getCompareTotalVisitsData;
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [data, setData] = useState<IData>();

    const domainsArray = useMemo(() => {
        return key ? key.split(",").filter(Boolean) : [];
    }, [key]);

    const { data: faviconsData } = useWebsitesFavicons(domainsArray, undefined, {
        enabled: isTotal && domainsArray.length > 0,
    });

    const transformData = useCallback((data: IMMXData, favicons: Record<string, string>): IData => {
        const totalData = data.Data[devicesTypes.TOTAL];
        let totalSearchVolume = 0;

        const parsedData = Object.entries(totalData)
            .map(([key, value]) => {
                const searchTotal = value[channels.ORGANIC] + value[channels.PAID];
                totalSearchVolume += searchTotal;
                return {
                    Domain: key,
                    Favicon: favicons[key],
                    SearchTotal: searchTotal,
                };
            })
            .map((site) => ({ ...site, ShareOfVisits: site.SearchTotal / totalSearchVolume }));

        return { data: { Data: parsedData, TotalCount: 0 } };
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            const totalVisitsData = await apiCall(apiParamsObj);
            setData({ data: totalVisitsData });
        };
        const fetchTotalData = async () => {
            const totalVisitsData = await apiCall(apiParamsObj);
            setData(transformData(totalVisitsData, faviconsData));
        };

        const res = isTotal ? fetchTotalData() : fetchData();
        res.catch(() => {
            setIsError(true);
        }).finally(() => {
            setIsLoading(false);
        });
    }, [isTotal, faviconsData, apiCall, apiParamsObj, transformData]);

    const propsToSend = {
        ...props,
        title,
        tooltip: tooltipsMap[webSource],
        fieldName,
        displayName,
        dashboardProps,
        trendCall,
        data,
        isLoading,
        isError,
    };

    return <SearchOverviewTopMiniFlexTableWrapper {...propsToSend} />;
};

export const SearchOverviewOrganicVSPaidCompareWrapper: FC<IWidgetSingleWrapperProps> = (props) => {
    const dashboardProps = {
        modelType: "fromWebsite",
        metric: "SearchOrganicPaidOverviewV2",
        type: "SearchOrganicPaidOverviewTable" as IWidgetModelTypesType,
    };
    const { apiParams, apiParamsObj } = props;
    const { webSource, key } = apiParams;
    const title = "ia.topkeywords.organicpaid";
    const tooltipsMap = {
        [devicesTypes.DESKTOP]: "analysis.source.search.overview.organicpaid.tooltip",
        [devicesTypes.MOBILE]: "analysis.source.search.overview.organicpaid.mobile.tooltip",
        [devicesTypes.TOTAL]: "analysis.source.search.overview.organicpaid.alltraffic.tooltip",
    };
    const fieldName = "OrganicPaidVisits";
    const displayName = "ia.topkeywords.organicpaid";
    const trendCall = PercentageBarCellRounded;
    const isDesktop = webSource === devicesTypes.DESKTOP;
    const apiCall = isDesktop ? getCompareOrganicVSPaid : getMMXTrafficData;
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [data, setData] = useState<IData>();

    const domainsArray = useMemo(() => {
        return key ? key.split(",").filter(Boolean) : [];
    }, [key]);

    const { data: faviconsData } = useWebsitesFavicons(domainsArray, undefined, {
        enabled: !isDesktop && domainsArray.length > 0,
    });

    const transformData = useCallback(
        (data: IMMXData, favicons: Record<string, string>): IData => {
            const totalData = data.Data[webSource];
            const parsedData = Object.entries(totalData).map(([key, value]) => {
                const searchSum = value[channels.ORGANIC] + value[channels.PAID];

                return {
                    Domain: key,
                    Favicon: favicons[key],
                    OrganicPaidVisits:
                        searchSum === 0
                            ? [0, 0]
                            : [value[channels.ORGANIC] / searchSum, value[channels.PAID] / searchSum],
                };
            });
            return { data: { Data: parsedData, TotalCount: 0 } };
        },
        [webSource],
    );

    useEffect(() => {
        const fetchData = async () => {
            const totalVisitsData = await apiCall(apiParamsObj);
            setData({ data: totalVisitsData });
        };

        const fetchMMXData = async () => {
            const totalVisitsData = await apiCall(apiParamsObj);
            setData(transformData(totalVisitsData, faviconsData));
        };

        const res = isDesktop ? fetchData() : fetchMMXData();
        res.catch(() => {
            setIsError(true);
        }).finally(() => {
            setIsLoading(false);
        });
    }, [isDesktop, faviconsData, apiCall, apiParamsObj, transformData]);

    const propsToSend = {
        ...props,
        title,
        tooltip: tooltipsMap[webSource],
        fieldName,
        displayName,
        dashboardProps,
        trendCall,
        data,
        isLoading,
        isError,
    };

    return <SearchOverviewTopMiniFlexTableWrapper {...propsToSend} />;
};

export const BrandedNonBrandedCompareWrapper: FC<IWidgetSingleWrapperProps> = (props) => {
    const dashboardProps = {
        modelType: "fromWebsite",
        metric: "BrandedKeywords",
        type: "Table" as IWidgetModelTypesType,
    };
    const { webSource, country, key, isWindow, isWWW, to, from } = props.apiParams;
    const title = "analysis.source.search.overview.brandednonbranded";
    const tooltip = "analysis.source.search.overview.brandednonbranded.tooltip";
    const fieldName = "BrandedNotBranded";
    const displayName = "analysis.source.search.overview.brandednonbranded.header";
    const trendCall = PercentageBarCellRounded;
    const apiCall = getCompareBrandedNonBranded;

    const [compareData, compareDataOps] = useLoading();

    useEffect(() => {
        compareDataOps.load(() => apiCall(props.apiParamsObj));
    }, [webSource, country, key, isWindow, isWWW, to, from]);
    const isOrganicVSPaidLoading = [useLoading.STATES.INIT, useLoading.STATES.LOADING].includes(compareData.state);
    const isOrganicVSPaidFetchError = compareData.state === useLoading.STATES.ERROR;

    const propsToSend = {
        ...props,
        title,
        tooltip,
        fieldName,
        displayName,
        dashboardProps,
        trendCall,
        apiCall,
        addMarginRight: false,
        isLoading: isOrganicVSPaidLoading,
        isError: isOrganicVSPaidFetchError,
        data: compareData,
    };
    return <SearchOverviewTopMiniFlexTableWrapper {...propsToSend} />;
};

const SearchOverviewTopMiniFlexTableWrapper: FC<IWidgetWrapperProps> = ({
    apiParams,
    subtitleFilters,
    title,
    tooltip,
    fieldName,
    displayName,
    trendCall,
    dashboardProps,
    addMarginRight,
    data,
    isError,
    isLoading,
}) => {
    const { webSource } = apiParams;
    const pageId = proNavigator.current().name;

    const transformData = (finalData) => {
        if (!isLoading && !isError) {
            return {
                ...finalData,
                data: {
                    TotalCount: finalData.data?.TotalCount,
                    Data: finalData.data.Data.map((site) => {
                        return {
                            ...site,
                            url: decodeURIComponent(proNavigator.href(pageId, { ...apiParams, key: site.Domain })),
                        };
                    }),
                },
            };
        } else {
            return { data: { Data: [] } };
        }
    };

    const A2D = () => (
        <AddToDashboard
            modelType={dashboardProps.modelType}
            metric={dashboardProps.metric}
            type={dashboardProps.type}
            webSource={webSource as IWidgetModelTypesWebSource}
        />
    );

    return (
        <SearchOverviewTopMiniFlexTable
            isLoading={isLoading}
            isError={isError}
            subtitleFilters={subtitleFilters}
            data={transformData(data).data}
            a2d={A2D}
            titleKey={title}
            tooltipKey={tooltip}
            fieldName={fieldName}
            displayName={displayName}
            trendCall={trendCall}
            addMarginRight={addMarginRight}
        />
    );
};
